import type { DealerContextProps } from "@porsche-customer/slfinrtl-context-types"
import { currentEnvironment } from "@porsche-customer/slfinrtl-environment"
import { useEffect } from "react"
import { getJwtPayload } from "src/common/jwt/jwtUtils"
import { getRuntimeContext } from "src/components/adobeAnalytics/getRuntimeContext"
import { trackLink } from "src/components/adobeAnalytics/trackLink/trackLink"

// manually downloaded from https://github.com/adobe/appmeasurement
const AA_SCRIPT_URL = "/static/scripts/AppMeasurement-v2.27.0.js"

// docs: https://porschedigital.atlassian.net/wiki/spaces/CSAL/pages/8146452609/AA+AppMeasurement+Implementation
enum AA_ACCOUNTS {
	PREVIEW = "pordigdadev",
	PROD = "pordigdaprod",
}
const AA_APPLICATION_ID = "dealer-app"

// overview of props and vars: https://porschedigital.atlassian.net/wiki/spaces/CSAL/pages/9307951177/DA+Custom+conversion
/* eslint-disable perfectionist/sort-interfaces */
export interface AppMeasurement {
	/** Sends a page view tracking call to Adobe Analytics. */
	t: () => void
	/**
	 * Tracks custom link clicks and interactions.
	 * @param linkObject - The link object being tracked
	 * @param linkType - Type of link: "e" (exit), "o" (other), or "d" (download)
	 * @param linkName - Human-readable name for the link
	 */
	tl: (linkObject: any, linkType: "e" | "o" | "d", linkName?: string) => void
	/** The name of the current page */
	pageName: string
	/** The url of the current page */
	pageURL?: string
	/** Currency code for monetary values */
	currencyCode: string
	/** Comma-separated list of events being tracked */
	events: string
	/** Comma-separated list of products */
	products?: string
	/** Unique identifier for completed purchase: https://experienceleague.adobe.com/en/docs/analytics/implementation/vars/page-vars/purchaseid */
	purchaseID?: string

	/** Page name tracking property */
	prop1: string
	/** Application identifier (always "pcw") */
	prop2: string
	/** Country code */
	prop3: string
	/** Language code */
	prop4: string
	/** User login status */
	prop5: string
	/** Current page URL */
	prop6: string
	/** Click element details */
	prop7?: string
	/** ID of the clicked element */
	prop8?: string
	/** Name of the clicked element */
	prop9?: string
	/** Type of the clicked element */
	prop10?: string
	/** Page name conversion variable */
	eVar1: string
	/** Application identifier conversion variable */
	eVar2: string
	/** Country conversion variable */
	eVar3: string
	/** Language conversion variable */
	eVar4: string
	/** Login status conversion variable */
	eVar5: string
	/** URL conversion variable */
	eVar6: string
	/** Company ID (Porsche Partner Number) */
	eVar7: string
	/** PPN organization ID (entry DN) */
	eVar8: string
	/** Dealer zip code */
	eVar9?: string
	/** Car engine type */
	eVar10?: string
	/** Whether the car is Porsche approved */
	eVar11?: boolean
	/** Unique listing identifier */
	eVar12?: string
	/** Unit of measurement for mileage */
	eVar13?: string
	/** Vehicle mileage value */
	eVar14?: number
	/** Model code or order type */
	eVar15?: string
	/** Vehicle model year */
	eVar16?: number
	/** Model name (fallback: model generation) */
	eVar17?: string
	/** Model range or series name */
	eVar18?: string
	/** Vehicle price */
	eVar19?: number
	/** Vehicle condition (new/used) */
	eVar20?: string
	/** Car type identifier (typically "real_car") */
	eVar21?: string
	/** Internal campaign reference (from `int_ref` query param) */
	eVar22?: string
	/** Internal campaign medium (from `int_medium` query param) */
	eVar23?: string
	/** Internal campaign id (from `int_id` query param) */
	eVar24?: string
	/** UTM source (from `utm_source` query param) */
	eVar25?: string
	/** UTM medium (from `utm_medium` query param) */
	eVar26?: string
	/** UTM campaign (from `utm_campaign` query param) */
	eVar27?: string
	/** ID of the clicked element */
	eVar28?: string
	/** Name of the clicked element */
	eVar29?: string
	/** Type of the clicked element */
	eVar30?: string
	/** Contact form identifier */
	eVar32?: string
	/** Internal campaign position (from `int_position` query param) */
	eVar41?: string
	/** UTM id (from `utm_id` query param) */
	eVar47?: string
	/** UTM content (from `utm_content` query param) */
	eVar48?: string
	/** UTM term (from `utm_term` query param) */
	eVar49?: string
	/** Internal campaign info (from `int_info` query param) */
	eVar68?: string
	/** Runtime context the app is running in: "STANDALONE" (installed PWA) | "BROWSER" | "SHOWROOM" | "MODEL" */
	eVar71?: string

	/** Vehicle data source */
	eVar196?: string
	/** Purchase request ID (used to measure open frequency per purchase request) */
	eVar197?: string
	/** Purchase request offer state: "with_offer" | "without_offer" | "no_offer" */
	eVar198?: string
	/** user ID */
	eVar199?: string
	/** application role  */
	eVar200?: string

	/** Variables to track with custom links */
	linkTrackVars: string
	/** Events to track with custom links */
	linkTrackEvents: string

	/** Enables the `doPlugins` callback; the library only invokes it when this is true */
	usePlugins: boolean
	/** Called by the library on every `t()`/`tl()` beacon to set variables just before sending */
	doPlugins: (s: AppMeasurement) => void
	/** Utility helpers provided by AppMeasurement */
	Util: {
		/** Reads a query parameter from the current URL */
		getQueryParam: (name: string) => string
	}
}
/* eslint-enable perfectionist/sort-interfaces */

declare global {
	let AppMeasurement: any
	interface Window {
		s?: AppMeasurement
	}
}

const scriptTagExists = (srcUrl: string): boolean => {
	const scriptTags = Array.from(document.getElementsByTagName("script"))
	return scriptTags.some((scriptTag) => scriptTag.src.includes(srcUrl))
}

const environment = currentEnvironment()
const lazilyAddAdobeAnalyticsScript = (dealerContext?: DealerContextProps): void => {
	const { dealershipId, locale, marketplaceKey, partnerNumber, username } = dealerContext ?? {}
	if (!scriptTagExists(AA_SCRIPT_URL)) {
		const script = document.createElement("script")
		script.async = true
		script.src = AA_SCRIPT_URL
		script.onload = () => {
			const accountId = environment === "prod" ? AA_ACCOUNTS.PROD : AA_ACCOUNTS.PREVIEW
			const s = new AppMeasurement(accountId)
			s.trackingServer = "analytics.porsche.com"
			//None for cookieless/without consent
			s.cookieLifetime = "None"
			s.trackDownloadLinks = true
			s.trackExternalLinks = true
			s.linkDownloadFileTypes = "pdf,doc,docx,xls,xlsx"

			// extract internal campaign and UTM parameters into eVars on every beacon
			s.usePlugins = true
			s.doPlugins = (am: AppMeasurement) => {
				am.eVar22 = am.Util.getQueryParam("int_ref")
				am.eVar23 = am.Util.getQueryParam("int_medium")
				am.eVar24 = am.Util.getQueryParam("int_id")
				am.eVar41 = am.Util.getQueryParam("int_position")
				am.eVar68 = am.Util.getQueryParam("int_info")
				am.eVar25 = am.Util.getQueryParam("utm_source")
				am.eVar26 = am.Util.getQueryParam("utm_medium")
				am.eVar27 = am.Util.getQueryParam("utm_campaign")
				am.eVar47 = am.Util.getQueryParam("utm_id")
				am.eVar48 = am.Util.getQueryParam("utm_content")
				am.eVar49 = am.Util.getQueryParam("utm_term")
			}

			s.prop2 = AA_APPLICATION_ID
			s.prop3 = marketplaceKey?.toUpperCase()
			s.prop4 = locale?.split("-")[0]

			s.eVar2 = AA_APPLICATION_ID
			s.eVar3 = marketplaceKey?.toUpperCase()
			s.eVar7 = partnerNumber
			s.eVar8 = dealershipId
			s.eVar71 = getRuntimeContext()
			s.eVar199 = username
			s.eVar200 = getJwtPayload()?.["ppn:groups"]

			window.s = s

			// global outbound links tracking for pds web components, regular anchor links are handled via trackExternalLinks setting above
			const pdsPrefix = "dealer-app"
			const relevantTagNames = ["p-link", "p-link-pure", "p-link-tile"].map((tagName) =>
				`${pdsPrefix}-${tagName}`.toUpperCase(),
			)

			document.addEventListener("click", (event) => {
				// Find the p-link or p-link-pure element in the event path
				const path = event.composedPath()
				const link = path.find((el) => relevantTagNames.includes((el as HTMLElement).tagName)) as HTMLElement

				if (link?.shadowRoot) {
					const href = link.shadowRoot.querySelector("a")?.href

					const isExternal = href && !href.includes(location.hostname)
					if (isExternal) {
						trackLink(href)
					}
				}
			})
		}

		document.head.appendChild(script)
	}
}

export interface AdobeAnalyticsProps {
	dealerContext?: DealerContextProps
}
export const AdobeAnalytics = ({ dealerContext }: AdobeAnalyticsProps) => {
	useEffect(() => {
		lazilyAddAdobeAnalyticsScript(dealerContext)
	}, [dealerContext])

	return null
}
